import type { IncompletePooler } from './types';

/**
 * Courriel de relance des poolers dont l'équipe est incomplète. Le dashboard
 * n'envoie rien : il prépare le texte et les adresses, que le gestionnaire
 * envoie depuis sa propre messagerie, par lots, en Cci.
 *
 * Les lots envoyés sont mémorisés dans le navigateur par identifiant de compte
 * (jamais par adresse courriel) pour reprendre plus tard sans doublon.
 */

/** Sous la limite habituelle de 500 destinataires par message (Gmail, Outlook). */
export const REMINDER_BATCH_SIZE = 400;
export const REMINDER_SENT_KEY = 'ci-reminder-sent';
export const REMINDER_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export const DEFAULT_POOL_LINK = 'https://hockeydynasty.net';

/** Les pools des clients s'ouvrent depuis leur site (iframe), pas depuis hockeydynasty.net. */
const POOL_LINKS: Record<number, string> = {
  138: 'https://arsenalsports.ca',
  152: 'https://toutsurlehockey.com',
};

export function poolLink(leagueId: number): string {
  return POOL_LINKS[leagueId] ?? DEFAULT_POOL_LINK;
}

export interface ReminderMessage {
  subject: string;
  body: string;
}

export function buildReminderMessage(leagueName: string, link: string): ReminderMessage {
  const pool = leagueName.trim() ? `« ${leagueName.trim()} »` : 'le pool';
  return {
    subject: `Votre équipe dans ${pool} n’est pas encore complète`,
    body: [
      'Bonjour,',
      '',
      `Votre équipe dans ${pool} n’est pas encore complète : il vous reste des joueurs à choisir.`,
      '',
      `Pour la compléter, connectez-vous au pool : ${link}`,
      '',
      'Bonne saison,',
      `L’équipe ${pool}`,
    ].join('\n'),
  };
}

export interface Recipient {
  email: string;
  username: string | null;
  joinedAt: string | null;
  /** Comptes qui partagent cette adresse (normalement un seul). */
  userIds: number[];
}

export interface RecipientList {
  recipients: Recipient[];
  /** Poolers sans adresse courriel utilisable. */
  withoutEmail: number;
}

const EMAIL_PATTERN = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/;

/** Adresses valides, sans doublon (casse ignorée), dans l'ordre reçu. */
export function buildRecipients(rows: IncompletePooler[]): RecipientList {
  const byEmail = new Map<string, Recipient>();
  let withoutEmail = 0;
  for (const row of rows) {
    const email = (row.email ?? '').trim();
    if (!EMAIL_PATTERN.test(email)) {
      withoutEmail += 1;
      continue;
    }
    const key = email.toLowerCase();
    const existing = byEmail.get(key);
    if (existing) {
      existing.userIds.push(row.user_id);
    } else {
      byEmail.set(key, { email, username: row.username, joinedAt: row.joined_at, userIds: [row.user_id] });
    }
  }
  return { recipients: [...byEmail.values()], withoutEmail };
}

export type SentReminders = Record<string, number>;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function sentKey(leagueId: number, userId: number): string {
  return `${leagueId}:${userId}`;
}

export function pruneSent(entries: SentReminders, now = Date.now()): SentReminders {
  const result: SentReminders = {};
  for (const [key, time] of Object.entries(entries)) {
    if (typeof time === 'number' && Number.isFinite(time) && now - time <= REMINDER_RETENTION_MS) {
      result[key] = time;
    }
  }
  return result;
}

export function loadSent(now = Date.now()): SentReminders {
  const raw = storage()?.getItem(REMINDER_SENT_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? pruneSent(parsed as SentReminders, now)
      : {};
  } catch {
    return {};
  }
}

export function saveSent(entries: SentReminders): void {
  try {
    storage()?.setItem(REMINDER_SENT_KEY, JSON.stringify(entries));
  } catch {
    // Stockage plein ou désactivé : le suivi reste valable pour la session.
  }
}

export function isSent(entries: SentReminders, leagueId: number, recipient: Recipient): boolean {
  return recipient.userIds.some((id) => sentKey(leagueId, id) in entries);
}

export function markSent(
  entries: SentReminders,
  leagueId: number,
  recipients: Recipient[],
  now = Date.now()
): SentReminders {
  const next = { ...pruneSent(entries, now) };
  for (const recipient of recipients) {
    for (const id of recipient.userIds) next[sentKey(leagueId, id)] = now;
  }
  return next;
}

export function unmarkSent(entries: SentReminders, leagueId: number, recipients: Recipient[]): SentReminders {
  const next = { ...entries };
  for (const recipient of recipients) {
    for (const id of recipient.userIds) delete next[sentKey(leagueId, id)];
  }
  return next;
}

export function clearLeagueSent(entries: SentReminders, leagueId: number): SentReminders {
  const prefix = `${leagueId}:`;
  return Object.fromEntries(Object.entries(entries).filter(([key]) => !key.startsWith(prefix)));
}

/** Prochain lot : les premiers destinataires pas encore relancés. */
export function nextBatch(
  recipients: Recipient[],
  entries: SentReminders,
  leagueId: number,
  size = REMINDER_BATCH_SIZE
): Recipient[] {
  return recipients.filter((r) => !isSent(entries, leagueId, r)).slice(0, size);
}

export function formatAddresses(recipients: Recipient[]): string {
  return recipients.map((r) => r.email).join(', ');
}

function csvCell(value: string): string {
  // Une valeur commençant par =, +, - ou @ serait interprétée comme une formule par un tableur.
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function recipientsCsv(recipients: Recipient[], entries: SentReminders, leagueId: number): string {
  const lines = [['Nom d’utilisateur', 'Courriel', 'Inscrit le', 'Relance envoyée'].join(',')];
  for (const r of recipients) {
    lines.push(
      [
        r.username ?? '',
        r.email,
        r.joinedAt ? r.joinedAt.slice(0, 10) : '',
        isSent(entries, leagueId, r) ? 'oui' : 'non',
      ]
        .map(csvCell)
        .join(',')
    );
  }
  return lines.join('\r\n');
}
